import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronRight, Plus, Search, Users, Archive, ArchiveRestore, Send, ShieldAlert } from "lucide-react";
import { LogInAsButton } from "@/components/LogInAsButton";
import { FmcsaRiskChip } from "@/components/FmcsaRiskChip";
import { formatEin } from "@/lib/ein";

const EQUIPMENT_LABELS = {
    reefer: "Reefer", dry_van: "Dry Van", conestoga: "Conestoga",
    flatbed: "Flatbed", step_deck: "Step Deck", other: "Other",
};

function fmtDate(s) {
    if (!s) return "—";
    try {
        return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch {
        return s;
    }
}

export default function AdminCustomersListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    // Phase TASK28E-REASSIGN-PREFILL — pass the source-rental id through
    // customer-link clicks so the destination page auto-opens the
    // "Create rental contract" dialog with the prefill hydrated.
    const reassignFrom = searchParams.get("reassign_from");
    const customerLinkQuery = reassignFrom ? `?reassign_from=${reassignFrom}` : "";
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [equipmentType, setEquipmentType] = useState("");
    const [statusFilter, setStatusFilter] = useState("active");
    const [equipmentTypes, setEquipmentTypes] = useState([]);
    const [saferRisk, setSaferRisk] = useState(searchParams.get("safer_risk") === "1");
    const [riskCount, setRiskCount] = useState(0);
    // Phase QCM-EXT — FMCSA carrier-risk filter. Allowed values:
    // "" (all) | "at_risk" | "warn". URL-synced so admins can share a
    // filtered view with a teammate.
    const [riskFilter, setRiskFilter] = useState(searchParams.get("risk") || "");

    const load = async () => {
        setLoading(true);
        try {
            const params = { status_filter: statusFilter };
            if (q) params.q = q;
            if (equipmentType) params.equipment_type = equipmentType;
            if (saferRisk) params.safer_risk = true;
            if (riskFilter) params.risk = riskFilter;
            const { data } = await api.get("/admin/customers", { params });
            setItems(data.items || []);
            setTotal(data.total || 0);
            setEquipmentTypes(data.equipment_types || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    // Always fetch the global risk-flag count so the toolbar chip is correct
    // regardless of the current filter.
    const loadRiskCount = async () => {
        try {
            const { data } = await api.get("/admin/stats");
            setRiskCount(data?.customers_with_safer_risk ?? 0);
        } catch {
            /* silent */
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter, equipmentType, saferRisk, riskFilter]);

    useEffect(() => {
        loadRiskCount();
    }, []);

    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        if (saferRisk) next.set("safer_risk", "1"); else next.delete("safer_risk");
        if (riskFilter) next.set("risk", riskFilter); else next.delete("risk");
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [saferRisk, riskFilter]);

    const onArchive = async (id) => {
        if (!window.confirm("Archive this customer? They can be restored later.")) return;
        try {
            await api.delete(`/admin/customers/${id}`);
            toast.success("Customer archived");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const onRestore = async (id) => {
        try {
            await api.post(`/admin/customers/${id}/restore`);
            toast.success("Customer restored");
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <DashboardShell>
            {/* Phase TASK28E-REASSIGN-PREFILL — banner when the list is
                acting as the target-customer picker for a reassign flow. */}
            {reassignFrom && (
                <div
                    className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm flex items-start gap-2"
                    data-testid="reassign-picker-banner"
                >
                    <span className="text-blue-900 font-semibold">Reassigning equipment.</span>
                    <span className="text-blue-900/80">
                        Pick a customer below to open the &quot;Create rental contract&quot; dialog with the source rental&apos;s fields pre-populated.
                    </span>
                </div>
            )}
            <div className="fade-up flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                        Customers
                    </p>
                    <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                        Customer directory
                    </h1>
                    <p className="mt-3 max-w-2xl text-sm text-slate-600 leading-relaxed">
                        Active customers are reused on returning-customer invites — their info auto-prefills new applications.
                    </p>
                </div>
                <Button asChild className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="customers-new-button">
                    <Link to="/admin/customers/new">
                        <Plus size={14} /> New customer
                    </Link>
                </Button>
            </div>

            <Card className="mt-8">
                <CardContent className="p-4 space-y-4">
                    <div className="flex flex-wrap gap-3 items-center">
                        <div className="relative flex-1 min-w-[200px]">
                            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <Input
                                data-testid="customers-search"
                                placeholder="Search company, EIN, USDOT, or email"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                className="pl-9"
                            />
                        </div>
                        <Button variant="outline" size="sm" onClick={load} data-testid="customers-search-go">Search</Button>
                        <div className="flex gap-1.5">
                            <button
                                type="button"
                                data-testid="customers-status-active"
                                onClick={() => setStatusFilter("active")}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                    statusFilter === "active" ? "bg-blue-900 text-white" : "bg-white border border-slate-200 text-slate-600"
                                }`}
                            >Active</button>
                            <button
                                type="button"
                                data-testid="customers-status-archived"
                                onClick={() => setStatusFilter("archived")}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium ${
                                    statusFilter === "archived" ? "bg-slate-700 text-white" : "bg-white border border-slate-200 text-slate-600"
                                }`}
                            >Archived</button>
                        </div>
                        <button
                            type="button"
                            data-testid="customers-safer-risk-toggle"
                            onClick={() => setSaferRisk((v) => !v)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${
                                saferRisk
                                    ? "bg-red-600 text-white border-red-600"
                                    : "bg-white text-slate-600 border-slate-200 hover:border-red-200"
                            }`}
                            title="Show only SAFER risk-flagged carriers"
                        >
                            <ShieldAlert size={12} /> SAFER risk
                            {riskCount > 0 && (
                                <span
                                    className={`ml-1 rounded-full px-1.5 text-[10px] font-semibold ${
                                        saferRisk ? "bg-white/20" : "bg-red-100 text-red-800"
                                    }`}
                                    data-testid="customers-safer-risk-count"
                                >
                                    {riskCount}
                                </span>
                            )}
                        </button>
                        {/* Phase QCM-EXT — FMCSA cached-snapshot risk filter. */}
                        <select
                            data-testid="customers-fmcsa-risk-filter"
                            value={riskFilter}
                            onChange={(e) => setRiskFilter(e.target.value)}
                            className="flex h-9 rounded-full border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700"
                            title="Filter by cached FMCSA risk signal"
                        >
                            <option value="">FMCSA: All</option>
                            <option value="at_risk">FMCSA: At risk</option>
                            <option value="warn">FMCSA: Caution</option>
                        </select>
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                        <button
                            type="button"
                            data-testid="customers-eq-all"
                            onClick={() => setEquipmentType("")}
                            className={`px-3 py-1 rounded-full text-xs ${
                                equipmentType === "" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                            }`}
                        >All equipment</button>
                        {equipmentTypes.map((et) => (
                            <button
                                key={et}
                                type="button"
                                data-testid={`customers-eq-${et}`}
                                onClick={() => setEquipmentType(et === equipmentType ? "" : et)}
                                className={`px-3 py-1 rounded-full text-xs ${
                                    et === equipmentType ? "bg-blue-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                                }`}
                            >{EQUIPMENT_LABELS[et] || et}</button>
                        ))}
                    </div>
                </CardContent>
            </Card>

            <div className="mt-6">
                {loading && <p className="text-sm text-slate-500" data-testid="customers-loading">Loading…</p>}
                {!loading && items.length === 0 && (
                    <Card className="border-dashed border-2 border-slate-200 bg-slate-50/60" data-testid="customers-empty">
                        <CardContent className="py-12 text-center">
                            <div className="mx-auto h-12 w-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400">
                                <Users size={20} />
                            </div>
                            <h3 className="mt-4 font-display text-lg font-semibold text-slate-900">
                                No customers found
                            </h3>
                            <p className="mt-2 max-w-sm mx-auto text-sm text-slate-600">
                                Customers are auto-created when an admin approves a submission, or you can create one manually.
                            </p>
                        </CardContent>
                    </Card>
                )}
                {!loading && items.length > 0 && (
                    <div className="overflow-x-auto rounded-md border border-slate-200 bg-white">
                        <table className="min-w-full text-sm" data-testid="customers-table">
                            <thead className="bg-slate-50 text-slate-600">
                                <tr className="text-xs uppercase tracking-wider">
                                    <th className="px-4 py-3 text-left">Company</th>
                                    <th className="px-4 py-3 text-left">EIN</th>
                                    <th className="px-4 py-3 text-left">Equipment</th>
                                    <th className="px-4 py-3 text-left">Last applied</th>
                                    <th className="px-4 py-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {items.map((c) => (
                                    <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <Link to={`/admin/customers/${c.id}${customerLinkQuery}`} className="font-medium text-slate-900 hover:text-blue-900" data-testid={`customer-link-${c.id}`}>
                                                    {c.company_name || "(no name)"}
                                                </Link>
                                                {c.safer_snapshot?.has_risk_flag && (
                                                    <span
                                                        data-testid={`customer-safer-chip-${c.id}`}
                                                        title={(c.safer_snapshot?.risk_reasons || []).join(", ") || "SAFER risk flag"}
                                                        className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-800 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-inset ring-red-200"
                                                    >
                                                        <ShieldAlert size={10} /> SAFER
                                                    </span>
                                                )}
                                                {/* Phase QCM-EXT — FMCSA cached-snapshot risk chip. */}
                                                <FmcsaRiskChip
                                                    risk={c.fmcsa_risk}
                                                    testId={`customer-fmcsa-risk-chip-${c.id}`}
                                                />
                                                {/* Phase J.4c (2026-07-29) — ACH activation status chip. */}
                                                {c.ach_activation_status === "sent" && (
                                                    <span
                                                        data-testid={`customer-ach-activation-chip-${c.id}`}
                                                        title="ACH activation link sent — awaiting customer verification"
                                                        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-900 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-inset ring-amber-200"
                                                    >ACH link sent</span>
                                                )}
                                                {c.ach_activation_status === "expired" && (
                                                    <span
                                                        data-testid={`customer-ach-activation-chip-${c.id}`}
                                                        title="ACH activation link expired — resend to unblock auto-pay"
                                                        className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-800 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-inset ring-red-200"
                                                    >ACH link expired</span>
                                                )}
                                                {c.ach_activation_status === "verified" && (
                                                    <span
                                                        data-testid={`customer-ach-activation-chip-${c.id}`}
                                                        title="Customer has a verified bank via Stripe Financial Connections"
                                                        className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 text-[10px] font-semibold px-2 py-0.5 ring-1 ring-inset ring-emerald-200"
                                                    >ACH verified</span>
                                                )}
                                            </div>
                                            {c.dba && <div className="text-xs text-slate-500">DBA: {c.dba}</div>}
                                        </td>
                                        <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                                            <div data-testid={`customer-ein-${c.id}`}>{c.ein_tax_id ? formatEin(c.ein_tax_id) : "—"}</div>
                                            {c.usdot_number && (
                                                <div className="text-[11px] text-slate-500 mt-0.5" data-testid={`customer-usdot-${c.id}`}>
                                                    USDOT {c.usdot_number}
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1" data-testid={`customer-equipment-${c.id}`}>
                                                {(c.equipment_types_used || []).length === 0 && <span className="text-slate-400">—</span>}
                                                {(c.equipment_types_used || []).slice(0, 2).map((et) => (
                                                    <span key={et} className="rounded-full bg-blue-50 text-blue-900 px-2 py-0.5 text-xs">
                                                        {EQUIPMENT_LABELS[et] || et}
                                                    </span>
                                                ))}
                                                {(c.equipment_types_used || []).length > 2 && (
                                                    <span
                                                        data-testid={`customer-equipment-more-${c.id}`}
                                                        title={(c.equipment_types_used || []).slice(2).map((et) => EQUIPMENT_LABELS[et] || et).join(", ")}
                                                        className="rounded-full bg-slate-100 text-slate-700 px-2 py-0.5 text-xs font-medium"
                                                    >
                                                        +{(c.equipment_types_used || []).length - 2} more
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-600">{fmtDate(c.last_application_at)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center justify-end gap-1">
                                                <LogInAsButton
                                                    customerId={c.id}
                                                    targetRole="customer"
                                                    labelOverride="Log in as"
                                                    dataTestid={`customer-login-as-${c.id}`}
                                                />
                                                <Button asChild size="sm" variant="outline" data-testid={`customer-view-${c.id}`}>
                                                    <Link to={`/admin/customers/${c.id}${customerLinkQuery}`}>View <ChevronRight size={12} /></Link>
                                                </Button>
                                                {c.status === "active" ? (
                                                    <Button size="sm" variant="outline" onClick={() => onArchive(c.id)} data-testid={`customer-archive-${c.id}`} aria-label="Archive customer">
                                                        <Archive size={12} />
                                                    </Button>
                                                ) : (
                                                    <Button size="sm" variant="outline" onClick={() => onRestore(c.id)} data-testid={`customer-restore-${c.id}`} aria-label="Restore customer">
                                                        <ArchiveRestore size={12} />
                                                    </Button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <p className="mt-3 text-xs text-slate-500">{total} total</p>
            </div>
        </DashboardShell>
    );
}
